/**
 * Web Components wrapper for React components
 * Enables simple HTML-like syntax for using React components in Razor views
 *
 * @example
 * ```html
 * <react-datatable
 *   data='[{"id":1,"name":"Test"}]'
 *   columns='[{"key":"name","title":"Name"}]'
 * ></react-datatable>
 * ```
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { logger } from '@/application/core';

/**
 * Registry to track mounted React roots for cleanup
 */
const mountedRoots = new WeakMap<HTMLElement, ReactDOM.Root>();

/**
 * Parse attribute value to appropriate JavaScript type
 */
function parseAttributeValue(value: string): any {
  // Try to parse as JSON first
  try {
    return JSON.parse(value);
  } catch {
    // If not valid JSON, return as string
    // Handle boolean strings
    if (value === 'true') return true;
    if (value === 'false') return false;
    // Handle null/undefined
    if (value === 'null') return null;
    if (value === 'undefined') return undefined;
    // Return as string
    return value;
  }
}

/**
 * Extract props from element attributes
 */
function extractProps(element: HTMLElement): Record<string, any> {
  const props: Record<string, any> = {};

  // Process all attributes
  for (let i = 0; i < element.attributes.length; i++) {
    const attr = element.attributes[i];
    const name = attr.name;
    const value = attr.value;

    // Skip standard HTML attributes and ref (handled separately)
    if (['id', 'class', 'style', 'ref'].includes(name)) {
      continue;
    }

    // Convert kebab-case to camelCase for React props
    const propName = name.replace(/-([a-z])/g, (g) => g[1].toUpperCase());

    // Parse the value
    props[propName] = parseAttributeValue(value);
  }

  // Handle className from class attribute
  if (element.className) {
    props.className = element.className;
  }

  // Check for direct property assignments (e.g., element.onRowClick)
  // This allows setting handlers without globals
  const elementAny = element as any;
  const propKeys = Object.keys(elementAny).filter(
    (key) => key.startsWith('on') && typeof elementAny[key] === 'function'
  );

  propKeys.forEach((key) => {
    props[key] = elementAny[key];
  });

  return props;
}

/**
 * Handle ref attribute - assigns element to window[refName]
 */
function handleRefAttribute(element: HTMLElement): void {
  const refAttr = element.getAttribute('ref');
  if (refAttr) {
    // Assign element to window object for easy access
    (window as any)[refAttr] = element;
  }
}

/**
 * Create event handlers from data attributes
 */
function extractEventHandlers(element: HTMLElement): Record<string, any> {
  const handlers: Record<string, any> = {};

  // Look for data-on-* attributes for event handlers
  for (let i = 0; i < element.attributes.length; i++) {
    const attr = element.attributes[i];
    const name = attr.name;

    if (name.startsWith('data-on-')) {
      const eventName = name.replace('data-on-', '');
      const handlerName = attr.value;

      // Convert kebab-case to camelCase, then to React event name
      // e.g., row-click -> rowClick -> onRowClick
      const camelCaseEvent = eventName.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
      const reactEventName =
        'on' + camelCaseEvent.charAt(0).toUpperCase() + camelCaseEvent.slice(1);

      // Try to get handler from element's custom property first (preferred - no globals)
      let handler = (element as any)[`__${handlerName}`];

      // Fallback to window object for backward compatibility
      if (!handler && typeof (window as any)[handlerName] === 'function') {
        handler = (window as any)[handlerName];
      }

      if (typeof handler === 'function') {
        handlers[reactEventName] = handler;
      } else {
        logger.error('WebComponents', 'Event handler not found:', {
          handlerName,
          reactEventName,
          checkedElement: !!(element as any)[`__${handlerName}`],
          checkedWindow: typeof (window as any)[handlerName],
        });
      }
    }
  }

  return handlers;
}

/**
 * Register a React component as a Custom Element (Web Component)
 *
 * @param tagName - The custom element tag name (must contain a hyphen)
 * @param ReactComponent - The React component to wrap
 * @param options - Configuration options
 */
export function registerReactComponent(
  tagName: string,
  ReactComponent: React.ComponentType<any>,
  options: {
    shadow?: boolean;
    observedAttributes?: string[];
  } = {}
): void {
  // Validate tag name
  if (!tagName.includes('-')) {
    throw new Error(`Custom element tag name "${tagName}" must contain a hyphen`);
  }

  // Check if already registered
  if (customElements.get(tagName)) {
    logger.warn('WebComponents', `Custom element "${tagName}" is already registered`);
    return;
  }

  class ReactWebComponent extends HTMLElement {
    private root: ReactDOM.Root | null = null;
    private componentRef: any = null;
    private dynamicProps: Record<string, any> = {};
    private reactContainer: HTMLDivElement | null = null;
    private originalContent: string = '';

    // Observed attributes for reactivity
    static get observedAttributes() {
      return options.observedAttributes || [];
    }

    constructor() {
      super();
      // Create a ref callback to capture the React component instance
      this.componentRef = React.createRef();

      // Set up property interceptors for common event handlers
      // This allows setting element.onRowClick = fn to work properly
      this.setupPropertyInterceptors();
    }

    /**
     * Setup property setters for event handlers to trigger re-renders
     */
    private setupPropertyInterceptors() {
      const eventHandlers = [
        'onRowClick',
        'onClick',
        'onChange',
        'onSubmit',
        'onConfirm',
        'onCancel',
        'onOpen',
        'onClose',
        'onSelect',
        'onDelete',
        'onEdit',
        'onSave',
      ];

      eventHandlers.forEach((handlerName) => {
        let value: any = undefined;

        Object.defineProperty(this, handlerName, {
          get: () => {
            return value || this.dynamicProps[handlerName];
          },
          set: (newValue: any) => {
            value = newValue;
            this.dynamicProps[handlerName] = newValue;

            // Re-render with the new prop if component is already mounted
            if (this.root) {
              this.mountReactComponent();
            }
          },
          enumerable: true,
          configurable: true,
        });
      });
    }

    connectedCallback() {
      // Element added to DOM
      logger.debug(tagName, 'connectedCallback - element added to DOM');
      handleRefAttribute(this);

      // Store the original innerHTML IMMEDIATELY before any modifications
      // Use setTimeout to ensure DOM is settled
      setTimeout(() => {
        if (!this.originalContent) {
          this.originalContent = this.innerHTML;
          logger.debug(tagName, 'Stored original content:', this.originalContent.length, 'chars');

          // Create a container div for React to render into
          if (!this.reactContainer) {
            this.reactContainer = document.createElement('div');
            this.reactContainer.style.cssText = 'width: 100%; height: 100%;';
            // Clear self and add container
            this.innerHTML = '';
            this.appendChild(this.reactContainer);
            logger.debug(tagName, 'Created react container div');
          }

          this.mountReactComponent();
        }
      }, 0);
    }

    disconnectedCallback() {
      // Element removed from DOM - cleanup
      this.unmountReactComponent();
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
      // Attribute changed - re-render if mounted
      if (this.root && oldValue !== newValue) {
        this.mountReactComponent();
      }
    }

    // Expose methods from the React component to the DOM element
    private exposeComponentMethods() {
      if (this.componentRef && this.componentRef.current) {
        const refObj = this.componentRef.current;

        // Copy all simple properties from the ref object to this element
        // Skip prototype binding to avoid "Illegal invocation" errors
        if (typeof refObj === 'object' && refObj !== null) {
          try {
            Object.keys(refObj).forEach((key) => {
              const value = refObj[key];
              // Only copy simple properties, not methods
              if (typeof value !== 'function') {
                (this as any)[key] = value;
              }
            });
          } catch (e) {
            logger.warn('WebComponents', 'Could not expose properties:', e);
          }
        }
      } else {
        logger.warn(
          'WebComponents',
          'componentRef or componentRef.current is null, cannot expose methods'
        );
      }
    }

    private mountReactComponent() {
      try {
        logger.debug(tagName, 'mountReactComponent called');

        // NOTE: Removed react-component-wrapper class - was causing freeze issues
        // when combined with postcss-prefix-selector and Radix Portal components

        // Extract props from attributes
        const props = extractProps(this);
        logger.debug(tagName, 'Extracted props:', props);

        const eventHandlers = extractEventHandlers(this);
        // Include dynamically set props (like element.onRowClick = fn)
        const allProps: Record<string, any> & { ref: any; children?: React.ReactNode } = {
          ...props,
          ...eventHandlers,
          ...this.dynamicProps,
          ref: this.componentRef,
        };

        // Pass original innerHTML content as children to the React component
        // This allows the web component's inner HTML to be rendered inside the React component
        if (this.originalContent && this.originalContent.trim()) {
          logger.debug(tagName, 'Adding children from originalContent');
          allProps.children = React.createElement('div', {
            dangerouslySetInnerHTML: { __html: this.originalContent },
          });
        }

        // Create or reuse root in the container div (not in the web component itself)
        if (!this.root && this.reactContainer) {
          logger.debug(tagName, 'Creating new React root in container');
          this.root = ReactDOM.createRoot(this.reactContainer);
          mountedRoots.set(this.reactContainer, this.root);
        }

        // Render React component with ref
        if (this.root) {
          logger.debug(tagName, 'Rendering with props:', {
            ...allProps,
            children: allProps.children ? '...' : 'none',
          });
          logger.debug(tagName, 'ReactComponent:', ReactComponent.name);
          this.root.render(React.createElement(ReactComponent, allProps));
          logger.debug(tagName, 'Component rendered successfully');
        } else {
          logger.error(tagName, 'Root not available for rendering');
        }

        // Expose component methods after React has had time to set the ref
        // Use multiple attempts with increasing delays to ensure ref is set
        const attemptExpose = (attempt = 1, maxAttempts = 10) => {
          setTimeout(() => {
            if (this.componentRef && this.componentRef.current) {
              this.exposeComponentMethods();
            } else if (attempt < maxAttempts) {
              attemptExpose(attempt + 1, maxAttempts);
            } else {
              logger.warn('WebComponents', 'Failed to expose methods after max attempts');
            }
          }, attempt * 50); // Exponential backoff: 50ms, 100ms, 150ms, etc.
        };

        attemptExpose();
      } catch (error) {
        logger.error('WebComponents', `Error mounting React component for <${tagName}>:`, error);
        this.innerHTML = `
          <div style="padding: 1rem; border: 1px solid #f44336; background: #ffebee; color: #c62828; border-radius: 4px;">
            <strong>Error rendering component:</strong> ${(error as Error).message}
          </div>
        `;
      }
    }

    private unmountReactComponent() {
      if (this.root) {
        // Unmount React component
        this.root.unmount();
        this.root = null;
        mountedRoots.delete(this.reactContainer!);
      }
    }
  }

  // Register the custom element
  logger.debug('WebComponents', `Defining custom element <${tagName}>`);
  customElements.define(tagName, ReactWebComponent);
  logger.debug('WebComponents', `Custom element <${tagName}> defined successfully`);
}

/**
 * Batch register multiple React components
 *
 * @param components - Object mapping tag names to React components
 */
export function registerReactComponents(
  components: Record<string, React.ComponentType<any>>
): void {
  logger.debug('WebComponents', 'Registering batch of components:', Object.keys(components));
  Object.entries(components).forEach(([tagName, component]) => {
    logger.debug('WebComponents', `Calling registerReactComponent for ${tagName}`);
    registerReactComponent(tagName, component);
    logger.debug('WebComponents', `Registered ${tagName}`);
  });
}

/**
 * Utility to check if Web Components are supported
 */
export function isWebComponentsSupported(): boolean {
  return 'customElements' in window && 'define' in window.customElements;
}

/**
 * Initialize Web Components system with polyfills if needed
 */
export async function initializeWebComponents(): Promise<void> {
  if (!isWebComponentsSupported()) {
    logger.warn('WebComponents', 'Web Components not supported, loading polyfills...');
    // In production, you might want to load polyfills here
    // For now, just warn the user
    throw new Error('Web Components not supported in this browser');
  }
}
